import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";

// Sort helper: sold-out clones last, otherwise keep incoming order.
const soldOut = (p) =>
  (p.availability || "preorder") !== "preorder" && (p.stock ?? 0) <= 0 ? 1 : 0;

export default function CloneCategory() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [clones, setClones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setStatus("loading");
    api
      .get(`/categories/${slug}`)
      .then(({ data }) => {
        if (!alive) return;
        setCategory(data);
        setStatus("ok");
        return api.get("/clones", { params: { category: data.slug } });
      })
      .then((res) => {
        if (alive && res) {
          const sorted = [...res.data].sort((a, b) => soldOut(a) - soldOut(b));
          setClones(sorted);
        }
      })
      .catch(() => alive && setStatus("notfound"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (status === "notfound") return <Navigate to="/shop/clones" replace />;

  // Slug changed (old link) → redirect to the current canonical slug.
  if (status === "ok" && category && category.slug !== slug) {
    return <Navigate to={`/shop/clones/category/${category.slug}`} replace />;
  }

  const name = category?.name || "";
  const desc =
    category?.description ||
    `Shop ${name} cannabis clones from Dark Growers Genetics — rooted, lab-tested cuttings shipped free next-day.`;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      {category && (
        <Seo
          title={`${name} Clones | Dark Growers Genetics`}
          description={desc}
          canonical={`https://darkgrowers.com/shop/clones/category/${category.slug}`}
        />
      )}
      <Link to="/shop/clones" data-testid="back-to-all-clones" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 text-sm mb-6">
        <ArrowLeft size={15} /> All Clones
      </Link>
      <div className="mb-10">
        <p className="label-caps text-emerald-500 mb-3">Clone Category</p>
        <h1 className="font-display text-5xl md:text-6xl text-slate-100 uppercase" data-testid="category-title">{name}</h1>
        {category?.description ? (
          <p className="text-slate-400 text-sm mt-5 max-w-3xl leading-relaxed" data-testid="category-description">
            {category.description}
          </p>
        ) : null}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-[#0F0F0F] animate-pulse" />
          ))}
        </div>
      ) : clones.length === 0 ? (
        <div className="text-center py-24" data-testid="category-empty">
          <p className="text-slate-400">No drops in this category right now — check back Friday.</p>
          <Link to="/shop/clones" className="inline-block mt-5 text-emerald-400 hover:text-emerald-300 text-sm">
            ← Back to All Clones
          </Link>
        </div>
      ) : (
        <>
          <p className="text-slate-500 text-xs mb-6" data-testid="category-count">
            {clones.length} {clones.length === 1 ? "clone" : "clones"}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {clones.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
